import axios from 'axios';

// API configuration
const API_KEY = process.env.REACT_APP_DEEPSEEK_API_KEY || "sk-d6f91cfc25684227944cec94daa5d011";
const BASE_URL = "https://api.deepseek.com/v1/chat/completions";

// Import all roles dynamically
const ROLES = {};

// Function to load role content
const loadRole = async (roleId) => {
  if (ROLES[roleId]) {
    return ROLES[roleId];
  }

  try {
    const roleModule = await import(`../../roles/${roleId}.js`);
    ROLES[roleId] = roleModule.default;
    return ROLES[roleId];
  } catch (error) {
    console.error(`Error loading role ${roleId}:`, error);
    throw new Error(`Invalid role: ${roleId}`);
  }
};

/**
 * Generate a completion using the DeepSeek API
 * @param {string} systemContent - The system role content
 * @param {string} userContent - The user's message
 * @returns {Promise<string>} The AI's response
 */
export const generateCompletion = async (systemContent, userContent) => {
  try {
    const response = await axios.post(BASE_URL, {
      model: 'deepseek-chat',
      messages: [
        { role: 'system', content: systemContent },
        { role: 'user', content: userContent }
      ],
      temperature: 0.7,
      max_tokens: 2000
    }, {
      headers: {
        'Authorization': `Bearer ${API_KEY}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data.choices[0].message.content;
  } catch (error) {
    console.error('API Error:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Format conversation history
 * @param {Array} history - Array of message objects
 * @returns {string} Formatted conversation history
 */
const formatHistory = (history) => {
  return history
    .map(msg => `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}`)
    .join('\n');
};

/**
 * Send a prompt to the AI
 * @param {string} prompt - The user's prompt
 * @param {string} promptType - The role/prompt type
 * @param {Array} history - Conversation history
 * @returns {Promise<string>} The AI's response
 */
export const sendPrompt = async (prompt, promptType, history = []) => {
  try {
    // Get role content
    const roleId = promptType.replace('.js', '');
    const roleContent = await loadRole(roleId);

    // Format conversation history
    const formattedHistory = formatHistory(history);

    // Combine role content, history, and new prompt
    const fullPrompt = `${roleContent}\n\nConversation History:\n${formattedHistory}\n\nUser: ${prompt}`;

    // Generate response using the AI model
    const response = await generateCompletion(roleContent, fullPrompt);
    return response;
  } catch (error) {
    console.error('Error processing prompt:', error);
    throw error;
  }
};
